import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from '../routing';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import api from '../utils/api';
import { errorText, errorCode } from '../utils/errors';
import toast from 'react-hot-toast';
import { Shield, CreditCard, CheckCircle, Mail } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import PageHero from '../components/PageHero';
import { Reveal } from '../components/Motion';
import theme from '../theme';

// An unset key used to fall back to 'pk_test_placeholder'. loadStripe rejects on
// a key that isn't a real one, useStripe() then stays null forever, and handlePay
// bailed on `!stripe` with no toast and no state change — the Pay button simply
// did nothing, over and over. Pass null instead (Elements accepts it) so the
// component can see the key is missing and say so.
const STRIPE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = STRIPE_KEY ? loadStripe(STRIPE_KEY) : null;
// The self-hosted copy in public/fonts, referenced once from index.html.
// This used to be a Google Fonts URL injected per page, which disclosed every
// visitor's IP to a third party on every page load and contradicted the privacy
// policy's "no third-party requests" claim. Kept as a constant because several
// pages import it into an @import rule rather than a <link>.
const FONTS = '/fonts/fonts.css';

export default function Checkout() {
  return (
    <Elements stripe={stripePromise}>
      <CheckoutInner />
    </Elements>
  );
}

function CheckoutInner() {
  const { rentalId } = useParams();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation();

  const [rental, setRental] = useState(null);
  const [loading, setLoading] = useState(true);
  const [done, setDone] = useState(false);
  const [paying, setPaying] = useState(false);
  // Synchronous double-submit guard; `paying` alone can't stop two clicks landing
  // in the same tick before React re-renders the disabled button.
  const payingRef = useRef(false);
  // Card charged but the confirm call never came back. The Pay button stays down.
  const [chargeTaken, setChargeTaken] = useState(false);
  // Set when the server priced the rental differently from what the button showed.
  const [serverTotal, setServerTotal] = useState(null);
  // The booking can no longer be paid at all — the reservation expired, or it
  // was cancelled. Shown in the page, because the Pay button is now permanently
  // dead and a toast would take the reason away with it.
  const [notPayable, setNotPayable] = useState(false);

  const [discountCode, setDiscountCode] = useState('');
  const [discountResult, setDiscountResult] = useState(null);
  const [discountLoading, setDiscountLoading] = useState(false);
  // The intent from the first attempt. A declined card, a wrong CVC or a
  // dismissed 3DS sheet leaves it perfectly payable, and the retry has to
  // confirm THAT intent again — asking the server for another one is what got
  // the customer a 409 'Payment already created' and no way to pay at all.
  // A ref as well as state: the retry reads it inside the same handler, and
  // state set moments earlier has not landed yet.
  const [paymentDetails, setPaymentDetails] = useState(null);
  const paymentDetailsRef = useRef(null);
  // Set when the server refuses to take money because the address was never
  // proven. Not a toast: a toast disappears, and the customer is left staring at
  // a Pay button that will keep failing for a reason they can no longer read.
  const [unverifiedEmail, setUnverifiedEmail] = useState(null);
  const [resending, setResending] = useState(false);
  // Set when the account predates the Terms checkbox. Accounts are deliberately
  // not backfilled — a grandfathered acceptance would fabricate the evidence the
  // record exists to provide — so they are asked here, where the rental
  // agreement is actually concluded.
  const [needsTerms, setNeedsTerms] = useState(false);
  // The consumer's express request to start the rental inside the 14-day
  // withdrawal window, and the acknowledgement that the right falls away once
  // the rental has been fully provided (CRD Art. 7(3)/16(a)). It is its own
  // control, unticked by default, and the Pay button is inert without it: the
  // Terms used to claim this consent was given by collecting the extensions from
  // the locker, which is neither express nor given in advance.
  const [startRequest, setStartRequest] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [acceptingTerms, setAcceptingTerms] = useState(false);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet'; link.href = FONTS;
    document.head.appendChild(link);
    api.get(`/rentals/${rentalId}`)
      .then(setRental)
      .catch(() => navigate('/my-rentals'))
      .finally(() => setLoading(false));
    return () => document.head.removeChild(link);
  }, [rentalId]);

  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) return;
    setDiscountLoading(true);
    setDiscountResult(null);
    // serverTotal is the figure from an aborted attempt, and both the header
    // price and the Pay label read `serverTotal ?? total`. Left standing, a
    // freshly applied code moved the order summary but froze the button on the
    // old amount — and handlePay would then charge whichever one was lower.
    // Every path that changes discountResult has to drop it.
    setServerTotal(null);
    try {
      const result = await api.post('/discounts/validate', {
        code: discountCode,
        rental_days: rental?.rental_days,
        rental_price: rental?.total_rental_price
      });
      setDiscountResult(result);
      toast.success(result.message);
    } catch (err) {
      toast.error(errorText(err, t('checkout.invalidDiscount')));
    } finally {
      setDiscountLoading(false);
    }
  };

  const resendVerification = async () => {
    setResending(true);
    try {
      const res = await api.post('/auth/verify-email/resend');
      if (res.verified) {
        // Verified in another tab while this banner was up — let them straight on.
        setUnverifiedEmail(null);
        toast.success(t('checkout.emailAlreadyVerified'));
      } else {
        toast.success(t('checkout.verificationResent'));
      }
    } catch (err) {
      toast.error(errorText(err, t('checkout.verificationResendFailed')));
    } finally {
      setResending(false);
    }
  };

  const acceptTerms = async () => {
    setAcceptingTerms(true);
    try {
      await api.post('/auth/accept-terms', { accept_terms: true, confirm_age: true });
      setNeedsTerms(false);
      toast.success(t('checkout.termsAcceptedToast'));
    } catch (err) {
      toast.error(errorText(err, t('checkout.termsAcceptFailed')));
    } finally {
      setAcceptingTerms(false);
    }
  };

  const handlePay = async () => {
    // Returning silently here is what made the button look broken when Stripe
    // never loaded. Say which of the two it is.
    if (!stripe || !elements) {
      toast.error(STRIPE_KEY ? t('checkout.paymentLoading') : t('checkout.paymentUnavailable'));
      return;
    }
    // `paying` is state, so the disabled prop only takes effect after a re-render
    // — two clicks in the same tick both saw paying === false. A ref updates
    // synchronously and actually closes the window.
    if (payingRef.current) return;
    if (!startRequest) {
      toast.error(t('checkout.withdrawalConsentRequired'));
      return;
    }
    payingRef.current = true;
    setPaying(true);

    // Flipped the moment Stripe accepts the card. Everything after this point is
    // bookkeeping, and the customer's money is already gone.
    let charged = false;

    // Re-arm the button. Every path that has NOT taken money must call this, or
    // the customer is left with a dead screen and a reservation that only the
    // stale-booking cron will clear.
    const rearm = () => { payingRef.current = false; setPaying(false); };

    try {
      // A retry confirms the intent we already have. create-payment persists the
      // PaymentIntent id before it answers, so asking for another one used to
      // come back 409 'Payment already created' — after a declined card, a wrong
      // CVC or a dismissed 3DS sheet, that raw English string was the last thing
      // the checkout ever said and there was no other way to pay.
      let details = paymentDetailsRef.current;
      if (!details) {
        details = await api.post('/payments/create-payment', {
          rental_id: rentalId,
          service_start_request: true,
          ...(discountResult ? { discount_code: discountCode } : {})
        });
        paymentDetailsRef.current = details;
        setPaymentDetails(details);
      }

      // The button showed a total this component computed itself. If the server
      // priced it differently — the usual cause is a discount that hit its usage
      // limit between validating and paying — charging anyway takes an amount the
      // customer never agreed to, and the old code did exactly that while the
      // button still read the old figure. Stop and make them confirm the real one.
      // Recomputed here rather than closing over the render-scope `total`, which
      // is declared further down and behind two early returns. `serverTotal`
      // takes precedence for the same reason the Pay label uses it: after an
      // aborted attempt it IS the figure on the button, and comparing against the
      // pre-abort computation aborted the corrected attempt too, for ever.
      const shownTotal = serverTotal ?? (
        (discountResult ? discountResult.final_price : parseFloat(rental.total_rental_price)) +
        parseFloat(rental.deposit_amount)
      );

      const quotedTotal = parseFloat(details.total);
      if (Number.isFinite(quotedTotal) && Math.abs(quotedTotal - shownTotal) > 0.005) {
        setServerTotal(quotedTotal);
        setDiscountResult(null);
        toast.error(t('checkout.priceChanged', { amount: quotedTotal.toFixed(2) }), { duration: 10000 });
        rearm();
        return;
      }

      const { error, paymentIntent } = await stripe.confirmCardPayment(details.client_secret, {
        payment_method: { card: elements.getElement(CardElement) }
      });

      if (error) {
        toast.error(error.message);
        // The intent survives a decline and stays payable, so the next tap
        // confirms this same one with the corrected card.
        rearm();
        return;
      }

      charged = true;

      const result = await api.post('/payments/confirm', {
        rental_id: rentalId,
        payment_intent_id: paymentIntent.id
      });

      setRental(result.rental);
      setDone(true);
      toast.success(t('checkout.paymentConfirmed'));
    } catch (err) {
      if (charged) {
        // The card WAS charged and only the confirm call failed — a 500, a
        // network blip, or the 15s axios timeout. Telling the customer "payment
        // failed" and re-enabling the button invited a second full charge.
        // Leave it disabled; the backend reconciles from the Stripe webhook.
        setChargeTaken(true);
        toast.error(t('checkout.paymentTakenNotConfirmed'), { duration: 10000 });
        return;
      }
      const code = errorCode(err);
      if (code === 'WITHDRAWAL_CONSENT_REQUIRED') {
        setStartRequest(false);
        toast.error(t('checkout.withdrawalConsentRequired'));
      } else if (code === 'TERMS_NOT_ACCEPTED') {
        setNeedsTerms(true);
      } else if (code === 'EMAIL_NOT_VERIFIED') {
        // The pickup code is emailed. Paying with an unproven address is how a
        // customer ends up unable to open the box their extensions are in.
        setUnverifiedEmail(err.email || true);
      } else if (code === 'PAYMENT_ALREADY_SUCCEEDED') {
        // The money is in — usually this tab racing the Stripe webhook. Anything
        // that re-enables the button here invites a second full charge.
        setChargeTaken(true);
        toast.success(t('checkout.paymentAlreadyPaid'), { duration: 10000 });
        return;
      } else if (code === 'PAYMENT_NOT_RESUMABLE' || code === 'RENTAL_NOT_PAYABLE') {
        // The booking itself is gone — the reservation expired, or an admin
        // cancelled it. Shown in the page rather than as a toast: a dead Pay
        // button with a vanished explanation is the thing this whole round is
        // about.
        setNotPayable(true);
        return;
      } else {
        toast.error(errorText(err, t('checkout.paymentFailed')));
      }
      rearm();
    }
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 120, fontFamily: theme.font.display, fontSize: 24, color: theme.gold }}>
      {t('checkout.loading')}
    </div>
  );
  if (!rental) return null;

  const cardStyle = {
    hidePostalCode: true,
    style: {
      base: { fontSize: '16px', color: theme.ink, '::placeholder': { color: theme.taupe }, fontFamily: theme.font.body },
      invalid: { color: theme.red }
    }
  };

  // Once the intent exists the amount is fixed, so the discount box has nothing
  // left to change.
  const priceLocked = !!paymentDetails;
  const rentalPrice = parseFloat(rental.total_rental_price);
  const depositAmount = parseFloat(rental.deposit_amount);
  const discountAmt = discountResult ? discountResult.discount_amount : 0;
  const finalRental = discountResult ? discountResult.final_price : rentalPrice;
  const total = finalRental + depositAmount;
  // The rate this checkout's mandate authorises. Falls back to the schema
  // default rather than to nothing: a mandate paragraph with a blank where the
  // price belongs is worse than a slightly stale figure, and rentals created
  // before migration 032 have the column NULL.
  const lateFeeRaw = Number(rental.late_fee_per_day);
  const lateFeeNum = Number.isFinite(lateFeeRaw) && lateFeeRaw >= 0 ? lateFeeRaw : 15;
  const lateFeeRate = Number.isInteger(lateFeeNum) ? String(lateFeeNum) : lateFeeNum.toFixed(2);

  return (
    <div style={{ minHeight: '100vh', background: theme.cream, fontFamily: theme.font.body }}>
      {/* No stagger anywhere on this page: someone is about to be charged, and
          a payment total that slides in under the cursor is the one place on
          the site where motion could cost the customer money. One quiet
          entrance for the column, nothing that moves after that. */}
      <PageHero title={t('checkout.title')} subtitle={rental.product_name} height={240} />
      <Reveal style={{ maxWidth: 600, margin: '0 auto', padding: '40px 24px' }}>

        {done ? (
          <DoneStep rental={rental} navigate={navigate} t={t} />
        ) : (
          <>
            {/* Order summary */}
            <div style={{ background: theme.white, border: `1px solid ${theme.sand}`, borderRadius: 4, padding: 28, marginBottom: 24 }}>
              <h3 style={{ fontFamily: theme.font.display, fontSize: 22, margin: '0 0 20px', color: theme.ink }}>
                {t('checkout.orderSummary')}
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 14, color: theme.grey600 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>{rental.product_name} — {rental.rental_days} {t('checkout.days')}</span>
                  <span style={{ textDecoration: discountResult ? 'line-through' : 'none', color: discountResult ? theme.taupe : 'inherit' }}>
                    €{rentalPrice.toFixed(2)}
                  </span>
                </div>

                {discountResult && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: theme.green }}>
                    <span>{t('checkout.discount')} ({discountCode.toUpperCase()})</span>
                    <span>-€{discountAmt.toFixed(2)}</span>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {t('checkout.refundableDeposit')}
                    <span style={{ fontSize: 11, color: theme.gold, background: theme.goldWash, padding: '2px 6px', borderRadius: 10 }}>
                      {t('checkout.refundedOnReturn')}
                    </span>
                  </span>
                  <span>€{depositAmount.toFixed(2)}</span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, fontSize: 16, color: theme.ink, borderTop: `1px solid ${theme.sand}`, paddingTop: 12, marginTop: 4 }}>
                  <span>{t('checkout.totalToday')}</span>
                  <span>€{total.toFixed(2)}</span>
                </div>
              </div>

              {/* Discount code. Locked once a PaymentIntent exists: its amount is
                  fixed at the moment it is created, so a code applied afterwards
                  could not change what the card is charged — offering the box
                  would promise a reduction that would never arrive. */}
              <div style={{ marginTop: 20, paddingTop: 20, borderTop: `1px solid ${theme.sand}` }}>
                <p style={{ fontSize: 12, color: theme.grey400, marginBottom: 10, letterSpacing: '0.05em' }}>{t('checkout.discountCode')}</p>
                {priceLocked ? (
                  <p style={{ fontSize: 12, color: theme.grey400, lineHeight: 1.7, margin: 0 }}>
                    {t('checkout.priceLockedNote')}
                  </p>
                ) : (
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="text"
                    value={discountCode}
                    onChange={e => { setDiscountCode(e.target.value.toUpperCase()); setDiscountResult(null); setServerTotal(null); }}
                    placeholder={t('checkout.enterCode')}
                    disabled={!!discountResult}
                    style={{
                      flex: 1, padding: '10px 14px', border: `1px solid ${theme.sand}`, borderRadius: 2,
                      fontSize: 14, fontFamily: theme.font.body, letterSpacing: '0.05em',
                      background: discountResult ? theme.grey100 : theme.white, outline: 'none', color: theme.ink
                    }}
                  />
                  {discountResult ? (
                    <button
                      onClick={() => { setDiscountResult(null); setDiscountCode(''); setServerTotal(null); }}
                      style={{ padding: '10px 16px', background: theme.sand, color: theme.grey600, border: 'none', borderRadius: 2, fontSize: 12, cursor: 'pointer', fontFamily: theme.font.body, whiteSpace: 'nowrap' }}>
                      {t('checkout.remove')}
                    </button>
                  ) : (
                    <button
                      onClick={handleApplyDiscount}
                      disabled={discountLoading || !discountCode.trim()}
                      style={{ padding: '10px 16px', background: discountLoading || !discountCode.trim() ? theme.taupe : theme.ink, color: theme.white, border: 'none', borderRadius: 2, fontSize: 12, cursor: discountLoading || !discountCode.trim() ? 'not-allowed' : 'pointer', fontFamily: theme.font.body, whiteSpace: 'nowrap' }}>
                      {discountLoading ? '...' : t('checkout.apply')}
                    </button>
                  )}
                </div>
                )}
                {discountResult && !priceLocked && (
                  <p style={{ fontSize: 12, color: theme.green, marginTop: 8 }}>✓ {discountResult.message}</p>
                )}
              </div>
            </div>

            {/* Payment box */}
            <div style={{ background: theme.white, border: `1px solid ${theme.sand}`, borderRadius: 4, padding: 28 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <h3 style={{ fontFamily: theme.font.display, fontSize: 22, margin: 0, color: theme.ink }}>
                  {t('checkout.payment')}
                </h3>
                <span style={{ fontFamily: theme.font.display, fontSize: 28, color: theme.ink }}>
                  €{(serverTotal ?? total).toFixed(2)}
                </span>
              </div>

              <div style={{ border: `1px solid ${theme.sand}`, padding: 16, borderRadius: 4, marginBottom: 24 }}>
                <CardElement options={cardStyle} />
              </div>

              {/* No publishable key configured: the card field will never mount,
                  so say it up front instead of leaving a dead Pay button. */}
              {!STRIPE_KEY && (
                <div style={{ background: theme.redWashPale, border: `1px solid ${theme.red}`, borderRadius: 4, padding: 16, marginBottom: 16 }}>
                  <p style={{ fontSize: 13, color: theme.redDark, margin: 0, lineHeight: 1.6 }}>
                    {t('checkout.paymentUnavailableDetail')}
                  </p>
                </div>
              )}

              {chargeTaken && (
                <div style={{ background: theme.goldWash, border: `1px solid ${theme.gold}`, borderRadius: 4, padding: 16, marginBottom: 16 }}>
                  <p style={{ fontSize: 13, color: theme.ink, margin: 0, lineHeight: 1.6 }}>
                    {t('checkout.paymentTakenNotConfirmedDetail')}
                  </p>
                  <button
                    onClick={() => navigate('/my-rentals')}
                    style={{ marginTop: 12, padding: '10px 16px', background: theme.ink, color: theme.white, border: 'none', borderRadius: 2, fontSize: 12, cursor: 'pointer', fontFamily: theme.font.body }}>
                    {t('checkout.goToMyRentals')}
                  </button>
                </div>
              )}

              {/* The booking itself is gone. Persistent, and with the way out on
                  it: the Pay button below can never succeed again, and a toast
                  would leave someone tapping it with no idea why. */}
              {notPayable && (
                <div style={{ background: theme.redWashPale, border: `1px solid ${theme.red}`, borderRadius: 4, padding: 16, marginBottom: 16 }}>
                  <strong style={{ fontSize: 13, color: theme.redDark, display: 'block', marginBottom: 6 }}>
                    {t('checkout.notPayableTitle')}
                  </strong>
                  <p style={{ fontSize: 13, color: theme.redDark, margin: 0, lineHeight: 1.6 }}>
                    {t('checkout.notPayableBody')}
                  </p>
                  <Link to="/browse" style={{ display: 'inline-block', marginTop: 12, padding: '10px 16px', background: theme.ink, color: theme.white, borderRadius: 2, fontSize: 12, textDecoration: 'none', fontFamily: theme.font.body }}>
                    {t('checkout.startNewBooking')}
                  </Link>
                </div>
              )}

              {needsTerms && (
                <div style={{ background: theme.goldWash, border: `1px solid ${theme.goldPale}`, borderRadius: 3, padding: '16px 18px', marginBottom: 16 }}>
                  <strong style={{ fontSize: 13, color: theme.ink, display: 'block', marginBottom: 8 }}>
                    {t('checkout.termsTitle')}
                  </strong>
                  <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 12.5, color: theme.grey550, lineHeight: 1.6, marginBottom: 12, cursor: 'pointer' }}>
                    <input type="checkbox" checked={termsChecked} onChange={e => setTermsChecked(e.target.checked)} style={{ marginTop: 3 }} />
                    <span>
                      <Trans i18nKey="checkout.termsAccept">
                        I am 18 or over and I accept the <Link to="/terms" target="_blank" style={{ color: theme.gold }}>Terms of Service</Link> and the <Link to="/privacy" target="_blank" style={{ color: theme.gold }}>Privacy Policy</Link>.
                      </Trans>
                    </span>
                  </label>
                  <button type="button" onClick={acceptTerms} disabled={!termsChecked || acceptingTerms}
                    style={{ background: (!termsChecked || acceptingTerms) ? theme.grey240 : theme.ink, color: theme.white, border: 'none', padding: '9px 18px', borderRadius: 2, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', cursor: (!termsChecked || acceptingTerms) ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
                    {acceptingTerms ? t('checkout.termsAccepting') : t('checkout.termsConfirm')}
                  </button>
                </div>
              )}

              {/* The address was never proven, so the server will not take the
                  money. Shown here, above the button, and persistent: the pickup
                  code is emailed, and paying with a mistyped address leaves the
                  customer unable to open the box their extensions are sitting in.
                  A toast would vanish and leave them clicking a Pay button that
                  keeps failing for a reason they can no longer read. */}
              {unverifiedEmail && (
                <div style={{ background: theme.goldWash, border: `1px solid ${theme.goldPale}`, borderRadius: 3, padding: '16px 18px', marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <Mail size={15} color={theme.goldBrass} />
                    <strong style={{ fontSize: 13, color: theme.ink }}>{t('checkout.verifyEmailTitle')}</strong>
                  </div>
                  <p style={{ fontSize: 13, color: theme.grey550, lineHeight: 1.7, margin: '0 0 12px' }}>
                    {t('checkout.verifyEmailBody')}
                    {typeof unverifiedEmail === 'string' && (
                      <> <strong style={{ color: theme.ink }}>{unverifiedEmail}</strong></>
                    )}
                  </p>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button type="button" onClick={resendVerification} disabled={resending}
                      style={{ background: theme.ink, color: theme.white, border: 'none', padding: '9px 18px', borderRadius: 2, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', cursor: resending ? 'default' : 'pointer', fontFamily: 'inherit' }}>
                      {resending ? t('checkout.verificationSending') : t('checkout.resendVerification')}
                    </button>
                    {/* The address itself may be the problem, so the fix has to
                        be reachable from here and not only from the profile. */}
                    <Link to="/profile" style={{ display: 'inline-flex', alignItems: 'center', padding: '9px 18px', border: `1px solid ${theme.sand}`, borderRadius: 2, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: theme.ink, textDecoration: 'none' }}>
                      {t('checkout.wrongEmail')}
                    </Link>
                  </div>
                </div>
              )}

              {/* Pre-contractual information about the right of withdrawal, at the
                  place where it is legally required to be — immediately before
                  the order is placed — together with the express request that
                  makes the "service fully performed" exemption available at all.
                  Two separate statements in one control would be unprovable, so
                  the Terms acceptance stays where it is and this stands alone. */}
              <div style={{ background: theme.cream, border: `1px solid ${theme.sand}`, borderRadius: 3, padding: '18px 20px', marginBottom: 18 }}>
                <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 12.5, lineHeight: 1.7, color: theme.ink700, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={startRequest}
                    onChange={e => setStartRequest(e.target.checked)}
                    style={{ marginTop: 3, flexShrink: 0 }}
                  />
                  <span>
                    <Trans i18nKey="checkout.withdrawalConsent" components={{ b: <strong /> }} />
                  </span>
                </label>
                <p style={{ margin: '12px 0 0 28px', fontSize: 11.5, lineHeight: 1.7, color: theme.grey500 }}>
                  <Trans
                    i18nKey="checkout.withdrawalInfo"
                    components={{ wform: <Link to="/withdrawal-form" target="_blank" style={{ color: theme.gold }} /> }}
                  />
                </p>
                {/* The card is charged now and the same card is charged later for
                    any late-return days. PSD2 treats that second charge as
                    merchant-initiated, which requires the customer to have agreed
                    to it at the moment of the first one — §5 of the Terms said so,
                    the checkout did not. */}
                {/* The rate is interpolated, not written into the sentence. It
                    used to read "…for any extra days…up to the amount of your
                    deposit" and name no figure at all, while the product page
                    showed the €5 extension price — so the only number a customer
                    saw before agreeing to this mandate was a third of what the
                    mandate can actually take. Read off the RENTAL, which
                    snapshots the rate at booking (migration 032), so the
                    authorisation is worded with the rate it authorises even
                    after the product is re-priced. */}
                <p style={{ margin: '10px 0 0 28px', fontSize: 11.5, lineHeight: 1.7, color: theme.grey500 }}>
                  <Trans
                    i18nKey="checkout.mandateNote"
                    values={{ lateFee: lateFeeRate, deposit: depositAmount.toFixed(2) }}
                    components={{ b: <strong style={{ color: theme.ink700 }} /> }}
                  />
                </p>
              </div>

              {/* Disabled for good once the card has been charged — re-enabling it
                  is what turned a failed confirm into a second full charge. */}
              {/* `!stripe` covers both "still loading" and "never will" — either
                  way the click can do nothing, so don't offer it. */}
              <button onClick={handlePay} disabled={paying || chargeTaken || notPayable || !stripe || !startRequest} style={payBtn(paying || chargeTaken || notPayable || !stripe || !startRequest)}>
                {chargeTaken
                  ? t('checkout.paymentProcessing')
                  : paying
                    ? t('checkout.processing')
                    : !stripe
                      ? (STRIPE_KEY ? t('checkout.paymentLoading') : t('checkout.paymentUnavailable'))
                      : t('checkout.pay', { amount: (serverTotal ?? total).toFixed(2) })}
              </button>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', marginTop: 20, color: theme.grey400, fontSize: 12 }}>
                <Shield size={14} color={theme.gold} />
                {t('checkout.securedByStripe')}
              </div>
            </div>

            {process.env.NODE_ENV !== 'production' && (
              <p style={{ textAlign: 'center', color: theme.taupe, fontSize: 11, marginTop: 16 }}>
                {t('checkout.testCard')}
              </p>
            )}
          </>
        )}
      </Reveal>
    </div>
  );
}

function DoneStep({ rental, navigate, t }) {
  // Registering the pickup order with the cabinet can fail after the money has
  // been taken — three attempts, then ops get an alert. The customer used to get
  // a green tick and "your pickup code is ready" with no code anywhere on the
  // page: €265 paid, and a trip to a box that will not open.
  const hasCode = !!rental.pickup_take_code;
  return (
    <div style={{ textAlign: 'center', padding: '48px 0' }}>
      <CheckCircle size={56} color={theme.green} style={{ marginBottom: 24 }} />
      <h2 style={{ fontFamily: theme.font.display, fontSize: 36, color: theme.ink, marginBottom: 12 }}>
        {t('checkout.doneTitle')}
      </h2>
      <p style={{ color: theme.grey600, fontSize: 16, marginBottom: hasCode ? 40 : 24 }}>
        {hasCode ? t('checkout.doneSubtitle') : t('checkout.doneSubtitleNoCode')}
      </p>

      {!hasCode && (
        <div style={{ background: theme.goldWash, border: `1px solid ${theme.goldPale}`, borderRadius: 4, padding: 24, marginBottom: 32, textAlign: 'left' }}>
          <div style={{ fontSize: 13, color: theme.goldInk, lineHeight: 1.7 }}>
            {t('checkout.codePendingBody')}
          </div>
        </div>
      )}

      {hasCode && (
        <div style={{ background: theme.white, border: `2px solid ${theme.gold}`, borderRadius: 4, padding: 32, marginBottom: 32, display: 'inline-block' }}>
          <div style={{ fontSize: 12, color: theme.grey400, letterSpacing: '0.08em', marginBottom: 8 }}>{t('checkout.pickupCodeLabel')}</div>
          <div style={{ fontSize: 44, letterSpacing: '0.25em', fontWeight: 700, color: theme.ink }}>{rental.pickup_take_code}</div>
          <p style={{ color: theme.grey500, fontSize: 13, marginTop: 16, maxWidth: 260 }}>{t('checkout.enterCodeLocker')}</p>
        </div>
      )}

      <div style={{ background: theme.creamDeep, borderRadius: 4, padding: 24, textAlign: 'left', marginBottom: 32 }}>
        <h3 style={{ fontFamily: theme.font.display, fontSize: 20, marginBottom: 16 }}>{t('checkout.nextSteps')}</h3>
        {(hasCode ? [
          t('checkout.nextStep1'),
          t('checkout.nextStep2'),
          t('checkout.nextStep3'),
          t('checkout.nextStep4'),
        ] : [
          t('checkout.nextStepNoCode1'),
          t('checkout.nextStep1'),
          t('checkout.nextStep2'),
          t('checkout.nextStep4'),
        ]).map((step, i) => (
          <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 12 }}>
            <div style={{ width: 24, height: 24, borderRadius: '50%', background: theme.gold, color: theme.white, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, flexShrink: 0 }}>
              {i + 1}
            </div>
            <span style={{ color: theme.grey600, fontSize: 14 }}>{step}</span>
          </div>
        ))}
      </div>

      <button
        onClick={() => navigate('/my-rentals')}
        style={{ width: '100%', padding: '18px', background: theme.ink, color: theme.white, border: 'none', borderRadius: 2, fontSize: 13, letterSpacing: '0.15em', cursor: 'pointer', fontFamily: theme.font.body }}>
        {t('checkout.viewMyRental')}
      </button>
    </div>
  );
}

function payBtn(disabled) {
  return {
    width: '100%', padding: '18px',
    background: disabled ? theme.taupe : theme.ink,
    color: theme.white, border: 'none', borderRadius: 2,
    fontSize: 14, letterSpacing: '0.1em',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: theme.font.body, fontWeight: 500
  };
}
